import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { AlertController } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoginService } from '@uoa/auth';
import { BypassErrorService } from '@uoa/error-pages';
import { BehaviorSubject, Observable, combineLatest, filter, firstValueFrom, map, shareReplay, skip, switchMap, take } from 'rxjs';
import { NceaStandards, NceaSubject, ResponseDto, NceaSubjectDetail, NceaRankScoreCalc, StudentSavedGrades, NceaUncategorisedStandards } from 'src/app/domain';
import { NceaCalcStandard } from 'src/app/domain/ncea-calc-standard.model';
import { environment } from 'src/environments/environment';
import { AppStorageService } from '.';
import { UserService } from './user.service';

interface NceaPermissionMessage {
  permission: boolean;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class NceaService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(AppStorageService);
  private readonly userService = inject(UserService);
  private readonly loginService = inject(LoginService);
  private readonly bypassErrorService = inject(BypassErrorService);
  private readonly alertCtrl = inject(AlertController);
  private readonly nceaSubjects = new BehaviorSubject<NceaSubject[]>([]);
  private readonly toastCtrl = inject(ToastController);

  nceaSubjects$: Observable<NceaSubject[]>;

  private readonly nceaPermission = new BehaviorSubject<boolean>(true);
  hasNceaPermission$: Observable<boolean>;

  private readonly uncategorisedStandards = new BehaviorSubject<NceaUncategorisedStandards>(null);
  uncategorisedStandards$: Observable<NceaUncategorisedStandards>;

  private readonly calculatedTotalScore = new BehaviorSubject<string>('');
  calculatedTotalScore$: Observable<string>;

  private readonly calculatePushed = new BehaviorSubject<boolean>(false);
  calculatePushed$: Observable<boolean>;

  private readonly newlyAddedSubject = new BehaviorSubject<string>('');
  newlyAddedSubject$: Observable<string>;

  constructor() {
    this.initialise();
  }

  private toSortedSubjects(subjects: NceaSubject[]): NceaSubject[] {
    return subjects.sort((a, b) => {
      if (a.year === b.year) {
        return a.subjectName.localeCompare(b.subjectName);
      }

      return +a.year - +b.year;
    });
  }

  private initialise(): void {
    this.nceaSubjects$ = this.nceaSubjects.asObservable()
      .pipe(
        map(this.toSortedSubjects),
        shareReplay(1)
      );

    this.uncategorisedStandards$ = this.uncategorisedStandards.asObservable().pipe(shareReplay(1));

    this.storageService
      .getItem<NceaSubject[]>('nceaResults')
      .then((val) => {
        if (val) {
          this.nceaSubjects.next(val);
        }
      });

    this.hasNceaPermission$ = this.nceaPermission.pipe(shareReplay(1));
    this.calculatedTotalScore$ = this.calculatedTotalScore.asObservable().pipe(shareReplay(1));
    this.calculatePushed$ = this.calculatePushed.asObservable().pipe(shareReplay(1));
    this.newlyAddedSubject$ = this.newlyAddedSubject.asObservable().pipe(shareReplay(1));

  }

  initNcea(): void {
    this.userService.loggedIn$
      .pipe(
        filter((loggedIn) => loggedIn === true),
        take(1)
      )
      .subscribe(async (_) => {
        await this.getNceaPermission();
        this.loginService.getUserInfo();

        this.getSavedSubjectStandards()
          .pipe(take(1))
          .subscribe((result) => {
            const standards = result.message.subjectstandards;

            if (standards?.categorised.length > 0) {
              const subjects = this.nceaSubjects.getValue();

              this.mergeSubjectAndStandards(standards.categorised, subjects);
              this.nceaSubjects.next(subjects);
            }

            if (standards?.uncategorised) {
              this.uncategorisedStandards.next(standards.uncategorised);
            } else {
              this.uncategorisedStandards.next({});
            }
          });
      });

    const nceaRetrieved = sessionStorage.getItem('nceaRetrieved');
    combineLatest([this.userService.loggedIn$, this.hasNceaPermission$])
      .pipe(
        skip(1),
        untilDestroyed(this),
        filter(([loggedIn, permission]) => loggedIn && permission === true && nceaRetrieved !== 'true'),
        take(1),
        switchMap((_) => this.getNZQANceaStandards())
      )
      .subscribe(async (standards) => {
        if (standards['errorCode']) {
          await this.noNzqaAlert();
        } else {
          const uncategorised = standards.message.uncategorised;
          const categorised = standards.message.categorised;

          this.nceaSubjects
            .pipe(take(1))
            .subscribe((subjects) => {
              this.mergeSubjectAndStandards(categorised, subjects, true);

              for (const subject of subjects) {
                for (const standard of subject.standards) {
                  if (uncategorised[subject.year]) {
                    uncategorised[subject.year].standards = uncategorised[subject.year].standards.filter(
                      (std) => std.standardCode !== standard.standardCode
                    );
                  }
                }
              }

              this.nceaSubjects.next(subjects);

              setTimeout(() => this.saveSubjectStandards(subjects, uncategorised).pipe(take(1)).subscribe(), 1000);
            });

          this.uncategorisedStandards.next(uncategorised);
        }

        sessionStorage.setItem('nceaRetrieved', 'true');
      });
  }

  private mergeSubjectAndStandards(categorisedSubjects, subjects: NceaSubject[], fromNzqa = false): void {
    for (const categorisedSubject of categorisedSubjects) {
      const found = this.getSubjectIndex(subjects, categorisedSubject);

      if (found != -1) {
        const subject = subjects[found];
        for (const standard of categorisedSubject.standards) {
          if (fromNzqa) {
            standard.fromNzqa = true;
          }

          const index = this.getStandardIndex(subject?.standards, standard);
          if (index !== -1) {
            subject.standards[index] = { ...subject.standards[index], ...standard };
          } else {
            subject.standards.push(standard);
            const suggestionIndex = this.getStandardIndex(subject.standardSuggestions, standard);
            subject.standardSuggestions = subject.standardSuggestions.splice(suggestionIndex, 1);
          }
        }
      } else {
        if (fromNzqa) {
          categorisedSubject.fromNzqa = true;
          categorisedSubject.standardSuggestions = [];
          categorisedSubject.standards.forEach((standard) => standard.fromNzqa = true);
          this.updateSubjectWithSuggestedStandards(categorisedSubject);
        }
        subjects.push(categorisedSubject);
      }
    }
  }

  async noNzqaAlert(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'NCEA Error',
      message: `Sorry, we can't find any NCEA result for you at the moment. This could be because your result haven't 
      been released yet or you've not studied level 3 NCEA. <br/><br/>Try checking back once your results are released, or 
      if there's an error in your data.`,
      buttons: [{ text: 'Close', role: 'cancel' }],
    });
    await alert.present();
  }

  getSubjectIndex(subjectsList, subject) {
    return subjectsList.findIndex((sub) => sub.subjectCode === subject.subjectCode && sub.year === subject.year);
  }

  getStandardIndex(standards, standard) {
    return standards.findIndex((std) => std.standardCode === standard.standardCode);
  }

  updateSubjectsObservable(subjects): void {
    this.nceaSubjects.next(subjects);
  }

  async getNceaPermission(): Promise<void> {
    const response = await firstValueFrom(this.http.get<ResponseDto<NceaPermissionMessage>>(`${environment.apiUrl}ncea-permission`));

    if (response?.message) {
      this.nceaPermission.next(response.message.permission);
    }
  }

  saveNceaPermission(permission: boolean): void {
    this.http
      .put<ResponseDto<NceaPermissionMessage>>(`${environment.apiUrl}ncea-permission`, { permission })
      .subscribe((data) => this.nceaPermission.next(data.message.permission));
  }

  updateSubjectWithSuggestedStandards(subject: NceaSubject): void {
    this.fetchSubjectYearStandards(subject.year, subject.subjectCode)
      .pipe(take(1))
      .subscribe(({ message: original }) => {
        const selected = subject.standards.map((s) => s.standardCode);
        subject.standardSuggestions = original.standardSuggestions
          .filter((std) => !selected.includes(std.standardCode))
          .sort((a, b) => a.title.localeCompare(b.title));
      });
  }

  getSubjectYearStandards(year: string, subjectCode: string): void {
    this.fetchSubjectYearStandards(year, subjectCode)
      .pipe(take(1))
      .subscribe(({ message: subject }) => {
        subject.standards = [];
        this.nceaSubjects.pipe(take(1)).subscribe((data) => {
          this.nceaSubjects.next([...data, subject]);
        });

        subject.standardSuggestions.sort((a, b) => a.title.localeCompare(b.title));
        this.newlyAddedSubject.next(subject.subjectCode + subject.year);
      });
  }

  getNceaSubjects(year: string): Observable<ResponseDto<NceaSubjectDetail[]>> {
    const endpoint = `${environment.apiUrl}subjects/ncea/search-by-year?year=${year}`;
    this.bypassErrorService.bypassError(endpoint, [404, 0]);
    return this.http.get<ResponseDto<NceaSubjectDetail[]>>(endpoint);
  }

  private fetchSubjectYearStandards(year: string, subjectCode: string): Observable<ResponseDto<NceaSubject>> {
    return this.http.get<ResponseDto<NceaSubject>>(`${environment.apiUrl}standards/search-by-subject-year?year=${year}&subjectCode=${subjectCode}`);
  }

  getNZQANceaStandards(): Observable<ResponseDto<NceaStandards>> {
    const endpoint = `${environment.apiUrl}standards/search-by-ncea`;
    this.bypassErrorService.bypassError(endpoint, [500]);
    return this.http.get<ResponseDto<NceaStandards>>(endpoint);
  }

  saveSubjectStandards(standards: NceaSubject[], uncategorisedStandards: NceaUncategorisedStandards): Observable<ResponseDto> {
    return this.http.put<ResponseDto>(`${environment.apiUrl}standards/save`, {
      subjectstandards: { categorised: standards, uncategorised: uncategorisedStandards },
    });
  }

  getSavedSubjectStandards(): Observable<ResponseDto<StudentSavedGrades>> {
    return this.http.get<ResponseDto<StudentSavedGrades>>(`${environment.apiUrl}standards/save`);
  }

  getNceaCalculation(requestStandards: NceaCalcStandard[]): Observable<ResponseDto<NceaRankScoreCalc>> {
    return this.http.post<ResponseDto<NceaRankScoreCalc>>(`${environment.apiUrl}calculate/ncea`, requestStandards);
  }

  rankScoreDisplayUpdate(): void {
    this.calculatePushed.next(false);
    this.calculatedTotalScore.next('');
  }

  /**
   * Note, that this function only works because the observables below are already complete and do not require any
   * asynchronous work that would delay the subscription.
   */
  getRequestStandards() {
    const requestStandards: NceaCalcStandard[] = [];

    this.nceaSubjects$.pipe(take(1)).subscribe((subjects) => {
      for (const subject of subjects) {
        const testComponent = subject.testComponent;

        for (const std of subject.standards) {
          if (std.ueApproved !== false) {
            let result: string = null;

            if (std?.result) {
              result = std.result;
            } else if (std?.results?.length == 1) {
              result = std.results[0].code;
            }

            if (result) {
              const reqStd = new NceaCalcStandard(std.year, testComponent, std.standardCode, result, std.version, std.results[0].credit);
              requestStandards.push(reqStd);
            }
          }
        }
      }
    });

    this.uncategorisedStandards$.pipe(take(1)).subscribe((uncategorisedStdObject) => {
      const uncategorised = uncategorisedStdObject ? Object.values(uncategorisedStdObject) : [];

      for (const uncategorisedStds of uncategorised) {
        for (const standard of uncategorisedStds.standards) {
          let result: string = null;
          if (standard.ueApproved) {
            if (standard?.result) {
              result = standard.result;
            } else if (standard.results.length == 1) {
              result = standard.results[0].code;
            }

            if (result) {
              const reqStd = new NceaCalcStandard(
                standard.year,
                standard.subjects[0].testComponent,
                standard.standardCode,
                result,
                standard.version,
                standard.results[0].credit
              );
              requestStandards.push(reqStd);
            }
          }
        }
      }
    });

    return requestStandards;
  }

  async calculate() {
    const requestStandards = this.getRequestStandards();
    let hasResults = false;

    if (requestStandards.length === 0) {
      this.calculatedTotalScore.next(null);

    } else {
      //to determine if some results were set by user, if no, stop calculation
      this.nceaSubjects$.pipe(take(1)).subscribe(
        (subjects) => {
          hasResults = subjects.some(sub => sub.standards.some(std => std?.result && std.result != ''));
        }
      );

    }
    if (!hasResults) {
      const toast = await this.toastCtrl.create({
        message: 'Please add standards and grade for each subject',
        duration: 2500,
        position: 'bottom',
      });

      await toast.present();
      return;
    }
    this.getNceaCalculation(requestStandards)
      .pipe(take(1))
      .subscribe(({ message: { totalScore, nceaRankScore } }) => {
        this.calculatedTotalScore.next(totalScore);
        this.calculatePushed.next(true);

        combineLatest([this.nceaSubjects$, this.uncategorisedStandards$])
          .pipe(take(1))
          .subscribe(([nceaSubjects, uncategorised]) => {

            for (const returnedStd of nceaRankScore) {
              let stdFound = false;
              for (const subjects of nceaSubjects) {
                const standard = subjects.standards.find(
                  (std) =>
                    std.standardCode == returnedStd.standardCode &&
                    ((std.result && std.result == returnedStd.result) ||
                      (std.results.length === 1 && std.results[0].code == returnedStd.result))
                );

                if (stdFound === true && standard) {
                  standard.rankScore = undefined;
                  standard.include = false;
                } else if (standard) {
                  stdFound = true;
                  standard.rankScore = returnedStd.rankScore;
                  standard.include = returnedStd.include;
                }
              }

              if (!stdFound) {
                const years = Object.values(uncategorised);
                let isFound = false;

                for (const year of years) {
                  const standard = year.standards.find((std) =>
                    std.standardCode == returnedStd.standardCode &&
                    ((std.result && std.result == returnedStd.result) || std.results[0].code == returnedStd.result)
                  );

                  if (isFound && standard) {
                    standard.rankScore = undefined;
                    standard.include = false;
                  } else if (standard) {
                    isFound = true;
                    standard.rankScore = returnedStd.rankScore;
                    standard.include = returnedStd.include;
                  }
                }
              }
            }
          });
      });
  }
}
